import { useState } from 'react'

import type { IUpdateIapSubTypeArgs } from '.'
import type { ColDef, ValueFormatterParams, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IIap } from '~/types/iap'

import ActionMenu from '~/components/ActionMenu'
import { FormatTextCellRenderer } from '~/components/CellRenderers/TextCellRenderer'
import WithSubTextCellRenderer from '~/components/CellRenderers/WithSubTextCellRenderer'
import sharedHeaderTemplate from '~/components/HeaderTemplate'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import { flattenFeatures } from '~/utils/iap'
import { copyText } from '~/utils/navigator'
import { getNumberWithSpaces } from '~/utils/number'
import { actionsColDef, iapStatusColDef, withSubTextColDef } from '~/utils/table'

const ConsumableCheckbox = ({
  disabled,
  onUpdateChange,
  sub_type,
}: IIap & { disabled?: boolean; onUpdateChange: (isChecked: boolean) => void }): ReactElement => {
  const [isChecked, setIsChecked] = useState(sub_type === 'consumable')

  const handleChange = () => {
    setIsChecked(!isChecked)
    onUpdateChange(!isChecked)
  }

  return (
    <div className="checkbox-container">
      <CheckboxInput className="available-checkbox" disabled={disabled} isChecked={isChecked} onChange={handleChange} />
    </div>
  )
}

const CheckBoxCellRenderer = ({ context, data }: ICellRendererParams<IIap>): ReactElement => {
  if (data) {
    const handleUpdateChange = (isChecked: boolean) => {
      context.onConsumableCheck({ iapId: data.id, isChecked } as IUpdateIapSubTypeArgs)
    }

    return <ConsumableCheckbox {...data} disabled={data.device_type === 'ios'} onUpdateChange={handleUpdateChange} />
  }
  return <div />
}

const ActionsCellRenderer = ({ data }: ICellRendererParams<IIap>): ReactElement => {
  if (data) {
    const menu: Menu = {
      actions: [
        {
          action: () => copyText(data.sku, 'Sku copied to clipboard.'),
          label: 'Copy sku',
        },
      ],
    }

    if (data && data.device_type === 'ios') {
      menu.navigations = [
        {
          action: () =>
            window.open(
              `https://appstoreconnect.apple.com/apps/${data.app_store_id}/appstore/iaps/${data.store_id}`,
              '_blank'
            ),
          label: 'View Product on AppStoreConnect',
          type: 'external',
        },
      ]
    }

    return <ActionMenu menu={menu} placement="top" />
  }

  return <div />
}

const columns: ColDef[] = [
  withSubTextColDef(WithSubTextCellRenderer, {
    field: 'sku',
    headerComponentParams: {
      menuIcon: 'fa-bars',
      template: sharedHeaderTemplate({
        headerSubtitle: 'Name',
        headerSubtitleClassname: 'capitalized',
        headerTitle: 'In-App Purchase ID',
      }),
    },
    headerName: '',
    headerTooltip: 'In-App Purchase ID',
    sortable: false,
    valueGetter: (params: ValueGetterParams<IIap>): any => ({
      subText: params.data?.sku,
      text: params.data?.title,
    }),
  }),
  iapStatusColDef({
    field: 'status',
    headerTooltip: 'Status',
    sortable: false,
  }),
  {
    cellRenderer: FormatTextCellRenderer,
    field: 'price',
    headerName: 'Price',
    headerTooltip: 'Price',
    maxWidth: 130,
    minWidth: 90,
    sortable: false,
    suppressHeaderMenuButton: true,
    valueFormatter: (params: ValueFormatterParams<IIap>): string => {
      return params.value ? `$ ${getNumberWithSpaces(params.value)}` : '--'
    },
  },
  {
    cellRenderer: CheckBoxCellRenderer,
    cellStyle: { justifyContent: 'center' },
    field: 'sub_type',
    headerName: 'Consumable',
    headerTooltip: 'Consumable',
    resizable: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
    width: 140,
  },
  {
    field: 'unlockables',
    headerName: 'Unlockables',
    headerTooltip: 'Unlockables',
    sortable: false,
    suppressHeaderMenuButton: true,
    valueFormatter: ({ value }: ValueFormatterParams<IIap, IIap['unlockables']>): string =>
      value ? `${flattenFeatures(value).length.toString()} feature(s)` : '',
  },
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
